import {useEffect, useRef, useState} from 'react'
import {Map} from 'immutable'
import PropTypes from 'prop-types'
import {useDispatch, useSelector} from 'react-redux'
import {deleteResource, updateResource} from 'redux-json-api'

import AccessibleSmallModal from '../../../../../shared_components/mui_base_components/modals/accessible_small_modal/AccessibleSmallModal'
import Button from '../../../../../shared_components/mui_base_components/button/Button'
import DashboardHeading from '../../../../../shared_components/mui_base_components/dashboard_heading/DashboardHeading'
import LinkButton from '../../../../../shared_components/mui_base_components/link_button/LinkButton'
import useLifeStoryHero from './hooks/useLifeStoryHero'
import {enterLifeStoryFlow} from './lifeStoryFlowEntry'
import {scheduleLifeStoryDeletion, usePendingLifeStoryDeletion} from './lifeStoryPendingDeletion'
import refreshVaultItemResponses from './lifeStoryVaultRefresh'
import {trackLifeStoryEvent} from './lifeStoryEventLogger'

import {
  ETHICAL_WILL_ITEM_SLUG,
  LIFE_AND_LEGACY_CATEGORY_SLUG,
  LIFE_STORY_DISMISS_FLAG,
  LIFE_STORY_FLOW_PATH,
  latestDraft,
  latestEthicalWill,
  lifeStoryContinuePath,
  lifeStoryLockedForUser
} from './lifeStoryTools'

import PremiumAccessDenied from '../../../../../shared_components/PremiumAccessDenied'
import {newItemPath, stripLeadingSlashHash} from '../../../../../lib/urlTools'
import {currentUser, setViewedFlagV2} from '../../../../../lib/userTools'
import {useNotificationContext} from '../../../../../shared_components/notifications/NotificationContext'
import Logger from '../../../../../lib/NewLogger'

import './lifeStoryHero.scss'

/**
 * "My Story" module for the Dashboard and Life & Legacy "Recommended for
 * you" sections (mock's mls module).
 *
 * States:
 * - No ethical will yet: invites the user to begin the guided experience.
 * - Any story (draft or published): welcomes the user back and continues into
 *   the flow's review page.
 * - Dismissed (per-user viewed flag), fetch failed, or not the plan owner:
 *   renders the `fallback` (the next best actions) instead. Nothing renders
 *   while still loading so the fallback doesn't flash in and get replaced.
 *
 * Dismissing sets a per-user flag, so the module stays hidden everywhere; a
 * dismissal with an unpublished draft first asks whether to keep or discard it.
 */
const LifeStoryHero = props => {
  const dispatch = useDispatch()
  const {alwaysNotify} = useNotificationContext()
  const {loading, visible, ethicalWills} = useLifeStoryHero({userConfig: props.userConfig, ownEverplan: props.ownEverplan})
  const user = useSelector(state => currentUser({userConfig: props.userConfig, users: state.api.users}))
  const pendingDeletion = usePendingLifeStoryDeletion()
  const [confirmingDismiss, setConfirmingDismiss] = useState(false)
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const [upgradeRequired, setUpgradeRequired] = useState(false)
  const [statusMessage, setStatusMessage] = useState('')

  const visibleEthicalWills = pendingDeletion ?
    ethicalWills.filter(will => will.get('id') !== pendingDeletion.ethicalWillId) :
    ethicalWills

  const ethicalWill = latestEthicalWill(visibleEthicalWills)
  const draft = latestDraft(visibleEthicalWills)
  const hasStory = Boolean(ethicalWill)
  const previousHasStoryRef = useRef(null)
  const viewLoggedRef = useRef(false)

  const variant = props.variant || 'category'
  const logContext = variant === 'dashboard' ? 'dashboard' : 'category_page'

  // Logged once per page view, as whichever module the user first sees: the
  // invitation to begin, or the nudge to come back to a story already started.
  useEffect(() => {
    if (loading || !visible || viewLoggedRef.current) return

    viewLoggedRef.current = true
    trackLifeStoryEvent({
      eventName: hasStory ? 'view_my_story_hero_nudge' : 'view_my_story_hero_module',
      userConfig: props.userConfig,
      payload: {context: logContext}
    })
  }, [hasStory, loading, logContext, props.userConfig, visible])

  useEffect(() => {
    if (loading) return

    if (previousHasStoryRef.current === null) {
      previousHasStoryRef.current = hasStory
      return
    }

    if (previousHasStoryRef.current !== hasStory) {
      setStatusMessage(
        hasStory ?
          'My Story is back, picking up where you left off.' :
          'My Story now starts from the beginning.'
      )
    }

    previousHasStoryRef.current = hasStory
  }, [hasStory, loading])

  if (loading) return null
  if (!visible) return props.fallback || null

  const locked = lifeStoryLockedForUser(props.userConfig)

  const trackHeroClick = () => trackLifeStoryEvent({
    eventName: 'click_my_story_hero_module',
    userConfig: props.userConfig,
    payload: {context: logContext}
  })

  const beginStory = () => {
    Logger.log({name: 'clicked_begin_life_story', payload: {context: logContext, type: 'vault'}})
    trackHeroClick()

    if (locked) return setUpgradeRequired(true)

    enterLifeStoryFlow(LIFE_STORY_FLOW_PATH)
  }

  const continueStory = () => {
    Logger.log({name: 'clicked_continue_life_story', payload: {context: logContext, type: 'vault'}})
    trackHeroClick()

    if (locked) return setUpgradeRequired(true)

    enterLifeStoryFlow(lifeStoryContinuePath(ethicalWill.get('id')), {openPicker: true})
  }

  const pathname = props.location?.pathname || '/'

  const uploadHref = () => `#/${stripLeadingSlashHash(
    newItemPath({
      itemSlug: ETHICAL_WILL_ITEM_SLUG,
      slug: props.categorySlug || LIFE_AND_LEGACY_CATEGORY_SLUG,
      path: pathname
    })
  )}`

  const persistDismissal = () => {
    Logger.log({name: 'dismissed_life_story_module', payload: {context: logContext, type: 'vault'}})
    setViewedFlagV2({
      user,
      flagToSet: LIFE_STORY_DISMISS_FLAG,
      updateResource: resource => dispatch(updateResource(resource))
    })
  }

  const dismiss = () => {
    if (draft) {
      setConfirmingDismiss(true)
    } else {
      persistDismissal()
      const notificationMessage = ethicalWill ?
        'Hidden. Your story is safe in Life & Legacy.' :
        'Hidden. You can start My Story anytime from Life & Legacy.'

      alwaysNotify.shortSuccess(notificationMessage)
    }
  }

  const keepDraft = () => {
    setConfirmingDismiss(false)
    persistDismissal()
    alwaysNotify.shortSuccess('Hidden. Your draft is safe in Life & Legacy.')
  }

  // Deferred so the undo snackbar (mock's undo bar) can take it back: the
  // DELETE only fires once the undo window passes.
  const discardDraft = () => {
    scheduleLifeStoryDeletion({
      ethicalWillId: draft.get('id'),
      message: 'Your draft has been discarded.',
      performDelete: () => dispatch(deleteResource({type: 'ethical-wills', id: draft.get('id')}))
        .then(() => Logger.log({name: 'discarded_life_story_draft', payload: {context: logContext, type: 'vault'}}))
    })

    setConfirmingDismiss(false)
    persistDismissal()
  }

  const deleteStory = () => {
    setConfirmingDelete(false)

    scheduleLifeStoryDeletion({
      ethicalWillId: ethicalWill.get('id'),
      firstGroupId: ethicalWill.get('first-group-id'),
      message: 'Your story has been deleted.',
      performDelete: () => dispatch(deleteResource({type: 'ethical-wills', id: ethicalWill.get('id')}))
        .then(() => Logger.log({name: 'deleted_life_story', payload: {context: logContext, type: 'vault'}}))
        .then(() => refreshVaultItemResponses(props.userConfig.get('everplan-id')))
    })
  }

  const button = ethicalWill ?
    <Button disableRipple={false} onClick={continueStory}>Continue my story</Button> :
    <Button disableRipple={false} onClick={beginStory}>Begin my story</Button>

  const content = ethicalWill ?
    `Your story grows every time you add to it. Each answer is saved and stays
    private until you choose to share it. Come back whenever a memory feels
    ready — there's no finish line, just the next one.` :
    `The memories, lessons, and moments you want to pass on — answered one
    question at a time, written or spoken in your own voice. A keepsake for
    people in your life.`

  const linkButton = ethicalWill ?
    <LinkButton onClick={() => setConfirmingDelete(true)}>Discard my story</LinkButton> :
    <LinkButton href={uploadHref()}>Already written yours? Upload it.</LinkButton>

  const pill = ethicalWill ? null : <span className='life-story-hero-pill'>New</span>

  const title = ethicalWill ? 'Welcome back — your story’s taking shape' : 'Share your story, in your own words'

  return (
    <div className='life-story-hero'>
      <DashboardHeading>Recommended for you</DashboardHeading>
      <div aria-live='polite' className='life-story-hero-status' role='status'>
        {statusMessage}
      </div>
      <div className='life-story-hero-box'>
        <button aria-label='Dismiss My Story recommendation' className='life-story-hero-dismiss' onClick={dismiss}>
          &times;
        </button>
        <div className='life-story-hero-body'>
          {pill}
          <div className='life-story-hero-title-and-content-container'>
            <h3 className='life-story-hero-title'>{title}</h3>
            <p className='life-story-hero-copy'>{content}</p>
          </div>
          <div className='life-story-hero-actions'>
            {button}
            {linkButton}
          </div>
        </div>
      </div>
      {upgradeRequired && <PremiumAccessDenied exit={() => setUpgradeRequired(false)} />}
      <AccessibleSmallModal
        className='life-story-hero-confirm'
        labelledBy='ls-dismiss-title'
        onClose={() => setConfirmingDismiss(false)}
        open={confirmingDismiss}
        showCloser={false}>
        <h3 className='life-story-hero-confirm-dismiss-title' id='ls-dismiss-title'>
          Before you go &mdash;
        </h3>
        <p className='life-story-hero-confirm-dismiss-copy'>You have a story in progress. Keep your draft safe in Life &amp; Legacy, or discard it?</p>
        <div className='life-story-hero-confirm-actions'>
          <LinkButton onClick={discardDraft}>Discard the draft</LinkButton>
          <Button autoFocus disableRipple={false} onClick={keepDraft}>Keep my draft</Button>
        </div>
      </AccessibleSmallModal>
      <AccessibleSmallModal
        className='life-story-hero-confirm'
        labelledBy='ls-delete-title'
        onClose={() => setConfirmingDelete(false)}
        open={confirmingDelete}
        showCloser={false}>
        <h3 className='life-story-hero-confirm-delete-title' id='ls-delete-title'>
          Delete your entire story?
        </h3>
        <p className='life-story-hero-confirm-delete-copy'>
          This deletes every story, every recording, and removes access
          for anyone you've shared it with.
        </p>
        <div className='life-story-hero-confirm-actions'>
          <LinkButton onClick={deleteStory}>
            Delete everything
          </LinkButton>
          <Button autoFocus disableRipple={false} onClick={() => setConfirmingDelete(false)}>
            Keep my story
          </Button>
        </div>
      </AccessibleSmallModal>
    </div>
  )
}

LifeStoryHero.propTypes = {
  categorySlug: PropTypes.string,
  fallback: PropTypes.node,
  location: PropTypes.object,
  ownEverplan: PropTypes.bool,
  userConfig: PropTypes.instanceOf(Map),
  variant: PropTypes.oneOf(['category', 'dashboard'])
}

export default LifeStoryHero
